import React, { useState, useEffect } from 'react';
import ActionMenu from '../components/common/ActionMenu';
import { Pencil, Trash2 } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const CountdownManagement = () => {
  const [graduationDays, setGraduationDays] = useState([]);
  const [countdownSettings, setCountdownSettings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    graduation_day_id: '',
    is_active: false,
    title: 'Graduation Ceremony',
    message: 'Get ready for the big day!'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [daysRes, settingsRes] = await Promise.all([
        api.get('/graduation-days'),
        api.get('/countdown/settings')
      ]);
      
      const futureDays = daysRes.data.filter(day => new Date(day.ceremony_date) > new Date());
      setGraduationDays(futureDays);
      setCountdownSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.graduation_day_id) {
      toast.error('Please select a graduation day');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/countdown/settings', formData);
      toast.success(editingSetting ? 'Countdown updated successfully' : 'Countdown created successfully');
      setShowForm(false);
      setEditingSetting(null);
      setFormData({
        graduation_day_id: '',
        is_active: false,
        title: 'Graduation Ceremony',
        message: 'Get ready for the big day!'
      });
      fetchData();
    } catch (error) {
      toast.error('Error saving countdown');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      graduation_day_id: setting.graduation_day_id,
      is_active: setting.is_active,
      title: setting.title,
      message: setting.message || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this countdown setting?')) return;
    
    setLoading(true);
    try {
      await api.delete(`/countdown/settings/${id}`);
      toast.success('Countdown deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Error deleting countdown');
    } finally {
      setLoading(false);
    }
  };

  const getGraduationDayInfo = (graduationDayId) => {
    const day = graduationDays.find(d => d.id === graduationDayId);
    if (!day) return { date: 'N/A', venue: 'N/A' };
    return {
      date: new Date(day.ceremony_date).toLocaleDateString(),
      venue: day.venue || 'Venue TBD'
    };
  };

  const getStatusBadge = (isActive) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-600';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Graduation Countdown Management</h1>
        <Button onClick={() => {
          setEditingSetting(null);
          setFormData({
            graduation_day_id: '',
            is_active: false,
            title: 'Graduation Ceremony',
            message: 'Get ready for the big day!'
          });
          setShowForm(true);
        }}>
          + Add Countdown
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingSetting ? 'Edit Countdown' : 'Create New Countdown'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="label">Graduation Day *</label>
              <select
                className="input-field"
                value={formData.graduation_day_id}
                onChange={(e) => setFormData({...formData, graduation_day_id: e.target.value})}
                required
              >
                <option value="">-- Select Graduation Day --</option>
                {graduationDays.map(day => (
                  <option key={day.id} value={day.id}>
                    {new Date(day.ceremony_date).toLocaleDateString()} - {day.venue || 'Venue TBD'}
                  </option>
                ))}
              </select>
              {graduationDays.length === 0 && (
                <p className="text-xs text-yellow-600 mt-1">
                  No upcoming graduation days found. Please create a graduation day first.
                </p>
              )}
            </div>

            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., 1st Graduation Ceremony"
              required
            />

            <Input
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="e.g., Get ready for the big day!"
            />

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Activate Countdown Timer on Homepage</span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingSetting ? 'Update' : 'Create')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowForm(false);
                setEditingSetting(null);
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Graduation Day</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {countdownSettings.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No countdown settings found. Click "Add Countdown" to create one.
                </td>
              </tr>
            ) : (
              countdownSettings.map((setting) => {
                const dayInfo = getGraduationDayInfo(setting.graduation_day_id);
                return (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{dayInfo.date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{dayInfo.venue}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{setting.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{setting.message || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(setting.is_active)}`}>
                        {setting.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(setting.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <ActionMenu
                        label={`Actions for ${setting.title || 'countdown'}`}
                        items={[
                          { label: 'Edit', icon: Pencil, onClick: () => handleEdit(setting) },
                          {
                            label: 'Delete',
                            icon: Trash2,
                            variant: 'danger',
                            onClick: () => handleDelete(setting.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CountdownManagement;
